import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './ExpandableTable.module.css';
import * as FaIcons from 'react-icons/fa';

const ExpandableTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No data available',
  onSort,
  onRowClick,
  rowClickTogglesExpand = null,
  sortable = true,
  hoverable = true,
  className = '',
  renderExpandedContent,
  getRowId = (row) => row.boxitLocationId || row.locationId || row.id || row.IdPlataforma,
}) => {
  const shouldRowClickToggle = rowClickTogglesExpand ?? !!renderExpandedContent;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [expandedIds, setExpandedIds] = useState(new Set());

  const handleSort = (column) => {
    if (!column.sortable || !sortable) return;
    const direction =
      sortConfig.key === column.key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: column.key, direction });
    if (onSort) onSort(column.key, direction);
  };

  const toggleExpand = (e, row) => {
    e.stopPropagation();
    const id = getRowId(row);
    if (!id) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sortedData = useMemo(() => {
    if (onSort || !sortConfig.key || !data || data.length === 0) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, onSort]);

  const getSortIcon = (column) => {
    if (!column.sortable || !sortable) return null;
    if (sortConfig.key !== column.key) {
      return <FaIcons.FaSort className={styles.sortIcon} />;
    }
    return sortConfig.direction === 'asc' ? (
      <FaIcons.FaSortUp className={styles.sortIconActive} />
    ) : (
      <FaIcons.FaSortDown className={styles.sortIconActive} />
    );
  };

  const handleRowClick = (e, row) => {
    if (shouldRowClickToggle) {
      toggleExpand(e, row);
    } else if (onRowClick) {
      onRowClick(row);
    }
  };

  const tableClasses = [
    styles.table,
    hoverable && styles.hoverable,
    (shouldRowClickToggle || onRowClick) && styles.clickable,
    className,
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={styles.container}>
        <table className={tableClasses}>
          <thead>
            <tr>
              {renderExpandedContent && <th className={styles.thExpand} />}
              {columns.map((col) => (
                <th key={col.key} className={styles.th}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                {renderExpandedContent && <td className={styles.td}><div className="bx-skeleton" style={{ height: '20px', width: '20px' }} /></td>}
                {columns.map((col) => (
                  <td key={col.key} className={styles.td}>
                    <div className="bx-skeleton" style={{ height: '20px', width: '100%' }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={styles.container}>
        <table className={tableClasses}>
          <thead>
            <tr>
              {renderExpandedContent && <th className={styles.thExpand} />}
              {columns.map((col) => (
                <th key={col.key} className={styles.th}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
        </table>
        <div className={styles.empty}>
          <FaIcons.FaInbox size={48} className={styles.emptyIcon} />
          <p className={styles.emptyMessage}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <table className={tableClasses}>
        <thead>
          <tr>
            {renderExpandedContent && <th className={styles.thExpand} />}
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${styles.th} ${col.sortable && sortable ? styles.sortable : ''}`}
                onClick={() => handleSort(col)}
                style={{ width: col.width }}
              >
                <div className={styles.thContent}>
                  <span>{col.label}</span>
                  {getSortIcon(col)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => {
            const rowId = getRowId(row);
            const isExpanded = expandedIds.has(rowId);

            return (
              <React.Fragment key={rowId || rowIndex}>
                <tr
                  className={(shouldRowClickToggle || onRowClick) ? styles.clickable : ''}
                  onClick={(e) => handleRowClick(e, row)}
                >
                  {renderExpandedContent && (
                    <td
                      className={styles.tdExpand}
                      onClick={(e) => toggleExpand(e, row)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpand(e, row);
                        }
                      }}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                    >
                      <span className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}>
                        <FaIcons.FaChevronRight size={14} />
                      </span>
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={styles.td}>
                      {col.render ? col.render(row, rowIndex) : row[col.key]}
                    </td>
                  ))}
                </tr>
                {renderExpandedContent && isExpanded && (
                  <tr className={styles.expandedRow}>
                    <td colSpan={columns.length + 1} className={styles.expandedCell}>
                      <div className={styles.expandedContent}>
                        {renderExpandedContent(row)}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

ExpandableTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      // Column headers are rendered, not used as text: the incidents list
      // passes a styled <span> for the client column.
      label: PropTypes.node.isRequired,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onSort: PropTypes.func,
  onRowClick: PropTypes.func,
  rowClickTogglesExpand: PropTypes.bool,
  sortable: PropTypes.bool,
  hoverable: PropTypes.bool,
  className: PropTypes.string,
  renderExpandedContent: PropTypes.func,
  getRowId: PropTypes.func,
};

export default ExpandableTable;
